import { useEffect, useState } from 'react';

import { fetchAttachmentObjectUrl, isImageMime, listAttachments } from '../../lib/attachments.js';

/**
 * Deterministic decorative gradient for an entity that has no photo. Derives a
 * stable hue from a seed (id/name) so the same entity always gets the same
 * colour across tiles, cart thumbnails and the detail modal. Mid lightness
 * reads on both light and dark themes. (hsl, not a hex/theme token — it's a
 * placeholder image, not chrome.)
 */
export function itemGradient(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 360;
  const h2 = (h + 38) % 360;
  return `linear-gradient(135deg, hsl(${h} 58% 52%), hsl(${h2} 62% 42%))`;
}

/**
 * Fetch every image attachment for an entity as object URLs (oldest first),
 * revoking them on cleanup. `enabled` gates the network calls to users who can
 * read attachments (others just get the gradient fallback — no 403s).
 */
export function useEntityImages(
  entityType: string,
  entityId: string | null | undefined,
  enabled: boolean,
): string[] {
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    if (!enabled || !entityId) { setUrls([]); return undefined; }
    let active = true;
    const made: string[] = [];
    void (async () => {
      try {
        const list = await listAttachments(entityType, entityId);
        const imgs = list
          .filter((a) => isImageMime(a.mimeType))
          .sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
        const out: string[] = [];
        for (const img of imgs) {
          const u = await fetchAttachmentObjectUrl(img.id);
          if (!active) { URL.revokeObjectURL(u); return; }
          made.push(u);
          out.push(u);
        }
        if (active) setUrls(out);
      } catch {
        if (active) setUrls([]);
      }
    })();
    return () => { active = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [entityType, entityId, enabled]);
  return urls;
}

/** First image attachment for an item (or null) — for tiles / thumbnails. */
export function useItemImage(itemId: string | null | undefined, enabled: boolean): string | null {
  const urls = useEntityImages('item', itemId, enabled);
  return urls[0] ?? null;
}
